import React, { memo } from 'react'

import { Button, Close, Icon, Textarea } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, func, number, oneOfType, string } from 'prop-types'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import RatingRow from './Components/RatingRow/RatingRow'
import useFeedbackForm from './useFeedbackForm'

const closeIcon = <Icon svgComponent={Close} size={iconSize} color="darker" notSpaced />

function FeedbackForm({ runId, criteria, onClose }) {
  const t = useTranslation()
  const {
    scores, setScore, comment, handleCommentChange, isCommentRequired, canSubmit, isLoading, submit,
  } = useFeedbackForm({ runId, criteria })

  const commentLabel = (
    <span className="cgt-body-base-semibold model-feedback__comment-label">
      {t('viewer.modelFeedback.comment.label')}
      {isCommentRequired && (
        <span className="model-feedback__required" aria-hidden="true"> *</span>
      )}
    </span>
  )

  return (
    <form
      className="model-feedback__form"
      aria-labelledby="model-feedback-title"
      onSubmit={submit}
    >
      <Button
        type="ghost"
        size="tiny"
        variant="only-icon"
        actionType="button"
        customClass="model-feedback__close"
        icon={closeIcon}
        aria-label={t('viewer.modelFeedback.close')}
        onClick={onClose}
      />
      <div className="model-feedback__header">
        <h2 id="model-feedback-title" className="cgt-heading-100 model-feedback__title">
          {t('viewer.modelFeedback.title')}
        </h2>
        <p className="cgt-body-base model-feedback__subtitle">{t('viewer.modelFeedback.subtitle')}</p>
      </div>
      <div className="model-feedback__rows">
        {criteria.map(criterion => (
          <RatingRow
            key={criterion}
            criterion={criterion}
            label={t(`viewer.modelFeedback.criteria.${criterion}.label`)}
            hint={t(`viewer.modelFeedback.criteria.${criterion}.hint`)}
            value={scores[criterion]}
            onChange={setScore}
          />
        ))}
      </div>
      <Textarea
        id="model-feedback-comment"
        label={commentLabel}
        inputClassName="model-feedback__comment"
        className="cgt-body-base model-feedback__comment-field"
        placeholder={t('viewer.modelFeedback.comment.placeholder')}
        value={comment}
        required={isCommentRequired}
        onChange={handleCommentChange}
      />
      <div className="model-feedback__actions">
        <Button type="primary" size="small" actionType="submit" disabled={!canSubmit || isLoading}>
          {t('viewer.modelFeedback.submit')}
        </Button>
      </div>
    </form>
  )
}

FeedbackForm.propTypes = {
  runId: oneOfType([number, string]).isRequired,
  criteria: arrayOf(string).isRequired,
  onClose: func.isRequired,
}

export default memo(FeedbackForm)
